<template>
  <dd-header-card v-if="sendsEmail" header="Email instellingen" data-dusk="show-emailo-settings">
    <dd-card-stacked-list>
      <dd-card-list-row>
        <dd-card-list-item title="Versturen als gebruiker">
          <template v-if="loaded">
            <user-badge v-if="emailUser" v-bind:user="emailUser" v-bind:display-name="true"/>
            <template v-else>Onbekend</template>
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 200px;height: 35px;display: block; margin-bottom: -3px;"/>
        </dd-card-list-item>
        <dd-card-list-item title="E-mailaccount">
          <template v-if="loaded">
            <email-account-badge-component v-if="emailAccount" v-bind:email-account="emailAccount"/>
            <template v-else>Onbekend</template>
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 200px;height: 35px;display: block; margin-bottom: -3px;"/>
        </dd-card-list-item>
      </dd-card-list-row>
      <dd-card-list-row>
        <dd-card-list-item title="E-mail template">
          <template v-if="loaded">
            <template v-if="emailTemplate">{{
                emailTemplate.nameInSystem || emailTemplate.title || emailTemplate.name
              }}
            </template>
            <template v-else>
              <a-alert message="Geen e-mail template geconfigureerd" type="warning" :show-icon="true"/>
            </template>
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 25%;height: 20px;display: block;"/>
        </dd-card-list-item>
        <dd-card-list-item title="Opmaak (HTML-template)">
          <template v-if="loaded">
            <template v-if="htmlEmailTemplate">{{ htmlEmailTemplate.name || htmlEmailTemplate.title }}</template>
            <template v-else>Standaard</template>
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 25%;height: 20px;display: block;"/>
        </dd-card-list-item>
      </dd-card-list-row>
      <dd-card-list-row>
        <dd-card-list-item title="Verstuur binnen kantoortijden">
          <template v-if="loaded">
            {{ this.value.send_email_at_office_hours ? "Ja" : "Nee" }}
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 100%;height: 35px;display: block;margin-bottom: 10px;"/>
        </dd-card-list-item>
        <dd-card-list-item title="Staat het handmatig controleren van de verzendlijst aan">
          <template v-if="loaded">
            {{ this.value.needs_verification ? "Ja" : "Nee" }}
          </template>
          <Skeleton v-else
                    class="skeleton"
                    v-bind:loading="true"
                    style="width: 100%;height: 35px;display: block;margin-bottom: 10px;"/>
        </dd-card-list-item>
      </dd-card-list-row>
    </dd-card-stacked-list>
  </dd-header-card>
</template>

<script>
import { Skeleton } from "vue-loading-skeleton";
import UserBadgeComponent from "~/modules/user/components/BadgeComponent.vue";
import UserBadgeGroupComponent from "~/modules/user/components/BadgeGroupComponent";
import UserDepartmentBadgeGroupComponent from "~/modules/user_department/components/BadgeGroupComponent";
import EmailAccountBadgeComponent from "~/modules/email_account/components/BadgeComponent";
import { AutomationTypeEnum } from "~/pages/automation/enums";
import { mapActions, mapGetters } from "vuex";
import userStore from "~/modules/user/store";
import emailAccountStore from "~/modules/email_account/store";
import emailTemplateStore from "~/modules/email_template/store";
import htmlEmailTemplateStore from "~/modules/html_email_template/store";

export default {
  name: "ShowEmailSettingsComponent",
  props: {
    value: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      loading: true,
      loaded: false,
      emailUser: null,
      emailAccount: null,
      emailTemplate: null,
      htmlEmailTemplate: null,
    };
  },
  components: {
    Skeleton,
    UserBadgeComponent,
    EmailAccountBadgeComponent,
    UserBadgeGroupComponent,
    UserDepartmentBadgeGroupComponent,
  },
  created() {
    const USER_STORE_KEY = "$_user";
    // eslint-disable-next-line no-underscore-dangle
    if (!(USER_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(USER_STORE_KEY, userStore);
    }

    const EMAIL_ACCOUNT_STORE_KEY = "$_email_account";
    // eslint-disable-next-line no-underscore-dangle
    if (!(EMAIL_ACCOUNT_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(EMAIL_ACCOUNT_STORE_KEY, emailAccountStore);
    }

    const EMAIL_TEMPLATE_STORE_KEY = "$_email_template";
    // eslint-disable-next-line no-underscore-dangle
    if (!(EMAIL_TEMPLATE_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(EMAIL_TEMPLATE_STORE_KEY, emailTemplateStore);
    }

    const HTML_EMAIL_TEMPLATE_STORE_KEY = "$_html_email_template";
    // eslint-disable-next-line no-underscore-dangle
    if (!(HTML_EMAIL_TEMPLATE_STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(HTML_EMAIL_TEMPLATE_STORE_KEY, htmlEmailTemplateStore);
    }

    Promise.all([
      this.initializeEmailUser(),
      this.initializeEmailAccount(),
      this.initializeEmailTemplate(),
      this.initializeHtmlEmailTemplate(),
    ])
        .then()
        .catch()
        .finally(() => {
          this.loading = false;
          this.loaded = true;
        });
  },
  computed: {
    ...mapGetters({
      users: "$_automation/users",
      emailAccounts: "$_email_account/emailAccounts",
    }),
    globalAutomation() {
      if (
          this.value !== null
          && this.value !== undefined
          && this.value.global_automation !== null
          && this.value.global_automation !== undefined
          && this.value.global_automation.data !== null
          && this.value.global_automation.data !== undefined) {
        return this.value.global_automation.data;
      }

      return null;
    },
    isCampaign() {
      if (this.globalAutomation !== null) {
        return this.globalAutomation.type === AutomationTypeEnum.CAMPAIGN;
      }
      return this.value.type === AutomationTypeEnum.CAMPAIGN;
    },
    sendsEmail() {
      return this.value.send_email || this.isCampaign;
    },
  },
  methods: {
    ...mapActions({
      getUserById: "$_user/getUserById",
      getEmailAccountById: "$_email_account/getEmailAccountById",
      getEmailTemplateById: "$_email_template/getEmailTemplateById",
      getHtmlEmailTemplateById: "$_html_email_template/getHtmlEmailTemplateById",
    }),
    initializeEmailUser() {
      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_user !== null
          && this.value.email_user !== undefined
          && this.value.email_user.data !== null
          && this.value.email_user.data !== undefined
      ) {
        this.emailUser = this.value.email_user.data;

        return Promise.resolve();
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_user_id !== null
          && this.value.email_user_id !== undefined
      ) {
        return this.getUserById(this.value.email_user_id)
            .then((user) => {
              this.emailUser = user;
            });
      }

      return Promise.resolve();
    },
    initializeEmailAccount() {
      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_account !== null
          && this.value.email_account !== undefined
          && this.value.email_account.data !== null
          && this.value.email_account.data !== undefined
      ) {
        this.emailAccount = this.value.email_account.data;

        return Promise.resolve();
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_account_id !== null
          && this.value.email_account_id !== undefined
      ) {
        return this.getEmailAccountById(this.value.email_account_id)
            .then((emailAccount) => {
              this.emailAccount = emailAccount;
            });
      }

      return Promise.resolve();
    },
    initializeEmailTemplate() {
      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_template !== null
          && this.value.email_template !== undefined
          && this.value.email_template.data !== null
          && this.value.email_template.data !== undefined
      ) {
        this.emailTemplate = this.value.email_template.data;

        return Promise.resolve();
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.email_template_id !== null
          && this.value.email_template_id !== undefined
      ) {
        return this.getEmailTemplateById(this.value.email_template_id)
            .then((emailTemplate) => {
              this.emailTemplate = emailTemplate;
            });
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.global_automation !== null
          && this.value.global_automation !== undefined
          && this.value.global_automation.data !== null
          && this.value.global_automation.data !== undefined
          && this.value.global_automation.data.global_email_template !== null
          && this.value.global_automation.data.global_email_template !== undefined
      ) {
        this.emailTemplate = this.value.global_automation.data.global_email_template.data;

        return Promise.resolve();
      }

      return Promise.resolve();
    },
    initializeHtmlEmailTemplate() {
      if (
          this.value !== null
          && this.value !== undefined
          && this.value.html_email_template !== null
          && this.value.html_email_template !== undefined
          && this.value.html_email_template.data !== null
          && this.value.html_email_template.data !== undefined
      ) {
        this.htmlEmailTemplate = this.value.html_email_template.data;

        return Promise.resolve();
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.html_email_template_id !== null
          && this.value.html_email_template_id !== undefined
      ) {
        return this.getHtmlEmailTemplateById(this.value.html_email_template_id)
            .then((htmlEmailTemplate) => {
              this.htmlEmailTemplate = htmlEmailTemplate;
            });
      }

      if (
          this.value !== null
          && this.value !== undefined
          && this.value.global_automation !== null
          && this.value.global_automation !== undefined
          && this.value.global_automation.data !== null
          && this.value.global_automation.data !== undefined
          && this.value.global_automation.data.global_html_email_template !== null
          && this.value.global_automation.data.global_html_email_template !== undefined
      ) {
        this.htmlEmailTemplate = this.value.global_automation.data.global_html_email_template.data;

        return Promise.resolve();
      }

      return Promise.resolve();
    },
  },
};
</script>

<style scoped>
</style>
